import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import emailTemplatesService from '../services/emailTemplatesService';

const SAMPLE_DATA = {
  customer_name: 'Jane Doe',
  customer_email: 'jane@example.com',
  booking_id: 'BK-00123',
  listing_name: 'Luxury Ikoyi Mansion',
  check_in_date: '2026-06-01',
  check_out_date: '2026-06-05',
  total_price: '₦600,000',
  num_guests: '2',
  payment_method: 'Paystack',
  payment_reference: 'REF-TEST-001',
  amount_paid: '₦600,000',
  reset_link: 'https://roarhomes.com/reset?token=sample',
  expiry_time: '30 minutes',
};

function applyVariables(template, vars) {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) =>
    vars[key] !== undefined ? vars[key] : match
  );
}

function TemplateItem({ template, onEdit, onPreview }) {
  return (
    <div className="p-4 bg-muted/30 rounded-xl border border-border group hover:border-primary transition-all">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-primary/10 text-primary rounded-xl flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
            <Icon icon={template.icon || 'lucide:mail'} className="text-xl" />
          </div>
          <div>
            <p className="text-sm font-bold">{template.title}</p>
            <p className="text-xs text-muted-foreground italic">{template.description}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Last updated: {new Date(template.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          </div>
        </div>
        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onEdit(template)} className="p-2 hover:bg-card rounded-lg text-muted-foreground hover:text-primary transition-colors" title="Edit Template">
            <Icon icon="lucide:pencil" />
          </button>
          <button onClick={() => onPreview(template)} className="p-2 hover:bg-card rounded-lg text-muted-foreground hover:text-primary transition-colors" title="Preview Template">
            <Icon icon="lucide:eye" />
          </button>
        </div>
      </div>
    </div>
  );
}

function TemplateEditorModal({ template, onClose, onSave }) {
  const [subject, setSubject] = useState(template.subject || '');
  const [htmlBody, setHtmlBody] = useState(template.htmlBody || '');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = React.useRef(null);

  const insertVariable = (varName) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newBody = htmlBody.slice(0, start) + `{{${varName}}}` + htmlBody.slice(end);
    setHtmlBody(newBody);
    setDirty(true);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + varName.length + 4, start + varName.length + 4);
    }, 0);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(template.name, { subject, htmlBody });
      setDirty(false);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (dirty && !window.confirm('You have unsaved changes. Are you sure you want to close?')) return;
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl border border-border shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold">Edit Template: {template.title}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{template.description}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowPreview(true)} className="px-4 py-2 border border-border text-sm font-bold rounded-xl hover:bg-muted transition-colors flex items-center gap-2">
              <Icon icon="lucide:eye" /> Preview
            </button>
            <button onClick={handleClose} className="p-2 hover:bg-muted rounded-lg text-muted-foreground hover:text-foreground transition-colors">
              <Icon icon="lucide:x" className="text-xl" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 p-5 space-y-4 overflow-y-auto">
            <div className="space-y-2">
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Subject Line</label>
              <input
                type="text"
                value={subject}
                onChange={e => { setSubject(e.target.value); setDirty(true); }}
                className="w-full px-4 py-2.5 bg-muted border-none rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">HTML Body</label>
              <textarea
                ref={textareaRef}
                rows={16}
                value={htmlBody}
                onChange={e => { setHtmlBody(e.target.value); setDirty(true); }}
                className="w-full px-4 py-2.5 bg-muted border-none rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none resize-none font-mono text-xs"
              />
            </div>
          </div>

          <div className="w-52 border-l border-border p-4 overflow-y-auto shrink-0">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Variables</p>
            <p className="text-[10px] text-muted-foreground mb-3">Click to insert at cursor position:</p>
            <div className="flex flex-wrap gap-1">
              {(template.variables || []).map(v => (
                <button
                  key={v}
                  onClick={() => insertVariable(v)}
                  className="px-2 py-1 bg-primary/10 text-primary text-[10px] font-mono rounded-lg hover:bg-primary hover:text-white transition-colors"
                >
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-border flex justify-end gap-3">
          <button onClick={handleClose} className="px-5 py-2.5 border border-border text-muted-foreground rounded-xl text-xs font-bold hover:bg-muted transition-colors">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="px-5 py-2.5 bg-primary text-primary-foreground rounded-xl text-xs font-bold hover:opacity-90 disabled:opacity-50 transition-opacity">
            {saving ? 'Saving…' : 'Save Template'}
          </button>
        </div>
      </div>

      {showPreview && (
        <TemplatePreviewModal
          template={{ ...template, subject, htmlBody }}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}

function TemplatePreviewModal({ template, onClose }) {
  const previewSubject = applyVariables(template.subject || '', SAMPLE_DATA);
  const previewBody = applyVariables(template.htmlBody || '', SAMPLE_DATA);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
      <div className="bg-card rounded-2xl border border-border shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold">Template Preview</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{template.title}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg text-muted-foreground hover:text-foreground transition-colors">
            <Icon icon="lucide:x" className="text-xl" />
          </button>
        </div>
        <div className="p-5 flex-1 overflow-y-auto space-y-4">
          <div className="space-y-2">
            <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Subject</label>
            <div className="px-4 py-2.5 bg-muted rounded-xl text-sm">{previewSubject}</div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Email Preview</label>
            <div className="border border-border rounded-xl overflow-hidden">
              <div className="bg-muted px-4 py-2 border-b border-border">
                <p className="text-xs text-muted-foreground">Rendered with sample data</p>
              </div>
              <div className="p-6 bg-white min-h-32" dangerouslySetInnerHTML={{ __html: previewBody }} />
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-border flex justify-end">
          <button onClick={onClose} className="px-5 py-2.5 bg-primary text-primary-foreground rounded-xl text-xs font-bold hover:opacity-90 transition-opacity">
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewingTemplate, setPreviewingTemplate] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState('');
  const [smtpConfig, setSmtpConfig] = useState({
    smtpHost: '',
    smtpPort: '587',
    smtpUser: '',
    smtpPassword: '',
    smtpFromName: 'Roar Homes',
    smtpFromEmail: '',
    smtpEncryption: 'TLS',
  });
  const [smtpSaving, setSmtpSaving] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [tmplData] = await Promise.all([
          emailTemplatesService.getTemplates(),
        ]);
        setTemplates(tmplData);
        try {
          const smtp = await emailTemplatesService.getSmtpConfig();
          if (smtp) setSmtpConfig(prev => ({ ...prev, ...smtp }));
        } catch { /* smtp config may not exist yet */ }
      } catch (err) {
        console.error('Failed to load email templates', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const handleSaveTemplate = async (name, updates) => {
    await emailTemplatesService.updateTemplate(name, updates);
    setTemplates(prev => prev.map(t => t.name === name ? { ...t, ...updates, updatedAt: new Date().toISOString() } : t));
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    setTestResult('');
    try {
      const result = await emailTemplatesService.sendTestEmail();
      setTestResult(`Test email sent: ${result.message}`);
    } catch (err) {
      setTestResult(`Failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setSendingTest(false);
    }
  };

  const handleSaveSmtp = async () => {
    setSmtpSaving(true);
    try {
      await emailTemplatesService.updateSmtp(smtpConfig);
      alert('SMTP settings saved');
    } catch (err) {
      alert('Failed to save SMTP settings');
    } finally {
      setSmtpSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => <div key={i} className="h-16 bg-muted rounded-xl animate-pulse" />)}
      </div>
    );
  }

  return (
    <>
      {editingTemplate && (
        <TemplateEditorModal
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSave={handleSaveTemplate}
        />
      )}
      {previewingTemplate && (
        <TemplatePreviewModal
          template={previewingTemplate}
          onClose={() => setPreviewingTemplate(null)}
        />
      )}

      <div className="space-y-8">
        {/* SMTP Configuration */}
        {/* <div className="bg-card rounded-2xl border border-border shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-heading font-bold">SMTP Configuration</h2>
            <div className="flex gap-2">
              <button
                onClick={handleSendTest}
                disabled={sendingTest}
                className="px-4 py-2 border border-border text-sm font-bold rounded-xl hover:bg-muted transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Icon icon="lucide:send" />
                {sendingTest ? 'Sending…' : 'Send Test Email'}
              </button>
            </div>
          </div>

          {testResult && (
            <div className={`mb-4 px-4 py-2 rounded-xl text-sm ${testResult.startsWith('Failed') ? 'bg-destructive/10 text-destructive' : 'bg-green-500/10 text-green-700'}`}>
              {testResult}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {[
              { key: 'smtpHost', label: 'SMTP Host', placeholder: 'smtp.resend.com' },
              { key: 'smtpPort', label: 'Port', placeholder: '587' },
              { key: 'smtpUser', label: 'Username', placeholder: 'apikey' },
              { key: 'smtpFromName', label: 'From Name', placeholder: 'Roar Homes' },
              { key: 'smtpFromEmail', label: 'From Email', placeholder: 'bookings@roarhomes.com' },
            ].map(({ key, label, placeholder }) => (
              <div key={key} className="space-y-2">
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{label}</label>
                <input
                  type="text"
                  value={smtpConfig[key] || ''}
                  onChange={e => setSmtpConfig(prev => ({ ...prev, [key]: e.target.value }))}
                  placeholder={placeholder}
                  className="w-full px-4 py-2.5 bg-muted border-none rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none"
                />
              </div>
            ))}

            <div className="space-y-2">
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Password</label>
              <input
                type="password"
                value={smtpConfig.smtpPassword || ''}
                onChange={e => setSmtpConfig(prev => ({ ...prev, smtpPassword: e.target.value }))}
                placeholder="••••••••"
                className="w-full px-4 py-2.5 bg-muted border-none rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Encryption</label>
              <select
                value={smtpConfig.smtpEncryption || 'TLS'}
                onChange={e => setSmtpConfig(prev => ({ ...prev, smtpEncryption: e.target.value }))}
                className="w-full px-4 py-2.5 bg-muted border-none rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none"
              >
                <option value="None">None</option>
                <option value="TLS">TLS (STARTTLS)</option>
                <option value="SSL">SSL</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={handleSaveSmtp} disabled={smtpSaving} className="px-6 py-2.5 bg-primary text-primary-foreground rounded-xl text-xs font-bold hover:opacity-90 transition-opacity disabled:opacity-50">
              {smtpSaving ? 'Saving…' : 'Save SMTP Settings'}
            </button>
          </div>
        </div> */}

        {/* Email Templates */}
        <div className="bg-card rounded-2xl border border-border shadow-sm p-6">
          <h2 className="text-lg font-heading font-bold mb-6">Email Templates</h2>
          <div className="space-y-4">
            {templates.map(template => (
              <TemplateItem
                key={template.id}
                template={template}
                onEdit={setEditingTemplate}
                onPreview={setPreviewingTemplate}
              />
            ))}
            {templates.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">No templates found.</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
